import { store, persistor } from '@shared/redux';
import type { RootState } from '@shared/redux/root-state';
import type { AuthTokenResponse } from '@shared/api/generated-api';
import { Mutex } from 'async-mutex';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { waitRehydration } from './wait-rehydration';
import { broadcastHardLogout, broadcastTokens, refreshAcrossTabs } from './auth-coordinator';
import { hardResetAuth } from './hard-reset-auth';
import { authActions } from '@shared/redux/ducks/auth';

const mutex = new Mutex();
const rawBaseQuery = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: (headers, { getState }) => {
    const language = (getState() as RootState).appSettings.language || 'en';
    headers.set('X-Language', language);
    return headers;
  },
});

let inFlight: Promise<boolean> | null = null;
let inFlightForRefreshToken: string | null = null;

const FORCE_LOGOUT_STATUSES = new Set<number>([400, 401, 403]);

export const refreshToken = async (): Promise<boolean> => {
  const doActualNetworkRefresh = async () => {
    await waitRehydration();
    const current = store.getState().auth?.authInfo?.refresh_token;
    if (!current) return false;

    if (inFlight && inFlightForRefreshToken === current) return inFlight;

    const doRefresh = async () => {
      await mutex.waitForUnlock();
      const release = await mutex.acquire();
      try {
        const tokenNow = store.getState().auth?.authInfo?.refresh_token;
        if (!tokenNow) return false;

        inFlightForRefreshToken = tokenNow;

        const accessBefore = store.getState().auth?.authInfo?.access_token ?? null;

        const result = await rawBaseQuery(
          { url: '/auth/token/refresh', method: 'POST', body: { token: tokenNow } },
          { getState: store.getState } as any,
          {},
        );

        if ('data' in result && result.data) {
          const tokens = result.data as AuthTokenResponse;
          store.dispatch(authActions.setTokens(tokens));
          broadcastTokens(tokens);
          if (persistor?.flush) await persistor.flush();
          return true;
        }

        const status = Number((result as any)?.error?.status);

        await new Promise((r) => setTimeout(r, 150));
        const accessAfter = store.getState().auth?.authInfo?.access_token ?? null;
        if (accessAfter && accessAfter !== accessBefore) return true;

        if (FORCE_LOGOUT_STATUSES.has(status)) {
          await hardResetAuth({ broadcast: broadcastHardLogout });
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.error('Refresh failed (non-auth):', (result as any)?.error);
          }
        }
        return false;
      } finally {
        release();
        inFlight = null;
        inFlightForRefreshToken = null;
      }
    };

    inFlight = doRefresh();
    return inFlight;
  };

  return refreshAcrossTabs(doActualNetworkRefresh);
};
